import * as React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { ConnectionType, WebinarAutoRecording } from '../../../../../../api/connections.dao';
import { CommonModal } from '../../../../../global/modal/common/common-modal';
import { DefaultCloseModalButton } from '../../../../../global/modal/common/common-close-modal-buttons';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { SelectGroup } from '../../../../../global/select-group/select-group';
import { DefaultDatePicker } from '../../../../../global/default-datepicker/default-datepicker';
import { SelectTimezones } from '../../../../../global/select-timezone/select-timezones';
import DefaultButton from '../../../../../global/default-button/default-button';
import './create-zoom-webinar.scss';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { ApprovalType } from './create-zoom-webinar-form-data.viewmodel';
import { CreateZoomWebinarVM } from './create-zoom-webinar.viewmodel';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import {
    ZOOM_AUTOMATICALLY_RECORD_CONNECTION_LABEL_TEXT
} from '../../../../../_blueprints/conections-templates/connections-template-form/components/conections-list/conections-list.texts';
import { FormFieldInput } from '../../../../../global/form/form-field-input/form-field-input';
import { ConnectionVersion, EnablementStatus } from '../../../../../../constants/common.constants';
import {
    webinarAutoRegistrationInfoMessage,
    webinarSyncMeetingAttendeeInfoMessage,
    webinarSyncMeetingAttendeeInfoMessageV2
} from '../../../../../../constants/connections-labels.constants';

export interface IPresentationProps {
    vm: CreateZoomWebinarVM;
    isMeetingConnectionsForm?: boolean;
    isAutoSyncLimitReached?: boolean;
}

const CreateZoomWebinarModal = mobxReact.observer((props: IPresentationProps) => {
    const {
        vm,
        isMeetingConnectionsForm,
        isAutoSyncLimitReached
    } = props;

    const formVM = vm;
    const formData = formVM.createZoomWebinarFormDataVM;

    const titleLabel = vm.isEditMode
        ? <FormattedMessage id='oe.zoomWebinar.editTitle' defaultMessage='Edit Zoom Webinar' />
        : <FormattedMessage id='oe.zoomWebinar.createTitle' defaultMessage='Create Zoom Webinar' />
    ;
    const detailsBtnLabel = vm.isEditMode
        ? <FormattedMessage id='oe.zoomWebinar.editDetailsManually' defaultMessage='Edit Details Manually' />
        : <FormattedMessage id='oe.zoomWebinar.addDetailsManually' defaultMessage='Add Details Manually' />
    ;

    const showAutoRegAndAutoSyncToggles = [
        isMeetingConnectionsForm,
        !isAutoSyncLimitReached,
        hasPermission(Permission.CREATE_AUTOSYNC_CONNECTION)
    ].every(Boolean);

    const footer = <div className='buttons-block'>
        <DefaultButton
            text={detailsBtnLabel}
            modifier={['fat', 'cancel']}
            onClick={() => formVM.isEditMode
                ? formVM.updateWebinarConnectionManually()
                : formVM.createZoomWebinarManuallyHandle(showAutoRegAndAutoSyncToggles)
            }
        />
        <div className='buttons-block buttons-block--from-right'>
            <DefaultButton
                text={titleLabel}
                modifier={['fat', 'confirm-safe']}
                onClick={formVM.isEditMode
                    ? formVM.updateWebinarDetails
                    : formVM.createZoomWebinarHandle
                }
                disabled={formVM.getIsFormSaving()}
            />
            <DefaultCloseModalButton/>
        </div>
    </div>;

    return (
        <>
            <CommonModal
                title={titleLabel}
                isOpen={formVM.getIsFormShown()}
                onRequestClose={() => {
                    formVM.hideForm();
                    formVM.disableEditMode();
                }}
                className='oe-modal-width create-zoom-webinar-modal'
                classNameBody='body__scrollable'
                footer={footer}
            >
                <form className='oe-form'>
                    <div className='m-bottom-25px'>
                        {
                            formVM.isEditMode ? (
                                <div className='m-bottom-5px w-100'>
                                    <FormattedMessage id='oe.input.meetingId' description='Input label' defaultMessage='Meeting Id' />
                                    <p className='m-top-5px' style={{ fontWeight: 'bold' }}>{formVM.webinarZoomId}</p>
                                </div>
                            ) : null
                        }
                        <div className='oe-form__row'>
                            <FormField className='m-bottom-4px' label={
                                <FormattedMessage id='oe.input.startDateTime' description='Input label' defaultMessage='Start Date and Time'/>
                            }>
                                <DefaultDatePicker
                                    selected={formData.getStartDateTime()}
                                    onChange={date => { formData.setStartDateTime(date); }}
                                    minDate={formData.getStartMinDate()}
                                    maxDate={null}
                                    placeholderText='Select start date time'
                                    className='oe-form-field__control'
                                />
                            </FormField>

                            <FormField className='m-bottom-4px' label={
                                <FormattedMessage id='oe.input.endDateTime' description='Input label' defaultMessage='End Date and Time'/>
                            }>
                                <DefaultDatePicker
                                    selected={formData.getEndDateTime()}
                                    onChange={date => { formData.setEndDateTime(date); }}
                                    placeholderText='Select end date time'
                                    minDate={formData.getStartDateTime() || new Date()}
                                    maxDate={null}
                                    className='oe-form-field__control'
                                />
                            </FormField>
                        </div>
                        {formData.getFieldError('dates') &&
                        <span className='oe-form-field__error'>{formData.getFieldError('dates')}</span>}
                    </div>
                    <FormField label={
                        <FormattedMessage id='oe.input.timeZone' description='Input label' defaultMessage='Time Zone'/>
                    }>
                        <SelectTimezones
                            className='oe-form-field__control'
                            value={formData.getTimeZone()}
                            onChange={mobx.action(timeZone => formData.setTimeZone(timeZone))}
                        />
                    </FormField>
                    {
                        !formVM.isEditMode ? (
                            <FormField label={
                                <FormattedMessage id='oe.input.zoomAccountGroup' description='Input label' defaultMessage='Zoom Account Group' />
                            }>
                                <SelectGroup
                                    value={formData.getGroup()}
                                    type={ConnectionType.Webinar}
                                    onChange={groupId => formData.setGroup(groupId)}
                                    className='field w-100 oe-form-field__control'
                                />
                            </FormField>
                        ) : null
                    }

                    <FormField label={
                        <FormattedMessage id='oe.input.meetingTopic' description='Input label' defaultMessage='Meeting Topic'/>
                    }>
                        <input
                            className='oe-form-field__control'
                            value={formData.getMeetingTopic()}
                            onChange={e => { formData.setMeetingTopic(e.target.value); }}
                            onBlur={() => { formData.checkMeetingTopic(); }}
                        />
                        {formData.getFieldError('meetingTopic') &&
                        <span className='oe-form-field__error'>{formData.getFieldError('meetingTopic')}</span>}
                    </FormField>
                    <FormField label={
                        <FormattedMessage id='oe.common.descriptionOptional' description='Input label' defaultMessage='Description (optional)'/>
                    }>
                        <textarea
                            rows={5}
                            style={{ resize: 'vertical' }}
                            className='oe-form-field__control'
                            value={formData.getDescription() || ''}
                            onChange={({ target: { value } }) => formData.setDescription(value)}
                        />
                        {formData.getFieldError('description')
                            ? <span className='oe-form-field__error'>{formData.getFieldError('description')}</span>
                            : null
                        }
                    </FormField>
                    {!formVM.isEditMode &&
                        <>
                            <FormFieldInput.Toggle
                                overrideDefaultClassName
                                className='oe-form-field form-toggle'
                                isSelected={formData.autoRecording === WebinarAutoRecording.Cloud}
                                toggle={formData.toggleAutoRecording}
                                text={<Text
                                    className='m-left-8px'
                                    {...ZOOM_AUTOMATICALLY_RECORD_CONNECTION_LABEL_TEXT}
                                    values={{
                                        prefix: formData.autoRecording === WebinarAutoRecording.Cloud ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />}
                            />
                            {
                                showAutoRegAndAutoSyncToggles && (
                                    <>
                                        {formVM.getConnectionVersion() !== ConnectionVersion.V2 &&
                                            <FormField className='form-toggle'>
                                                <OEToggle
                                                    isSelected={formData.autoRegistrationEnabled}
                                                    onClick={formData.togglePassportAutoRegistration}
                                                    dataTestId='webinar-autoRegistration-toggle'
                                                />
                                                <Text
                                                    className={'m-left-8px'}
                                                    message={formData.autoRegistrationEnabled ? 'Passport Auto Registration enabled' : 'Passport Auto Registration disabled'}
                                                    id={formData.autoRegistrationEnabled ? 'oe.zoomWebinar.autoRegistrationEnabled' : 'oe.zoomWebinar.autoRegistrationDisabled'}
                                                />
                                                <InfoPopover className='m-left-8px'>
                                                     <div>{webinarAutoRegistrationInfoMessage}</div>
                                                </InfoPopover>
                                            </FormField>
                                        }
                                        <FormField className='form-toggle'>
                                            <OEToggle
                                                isDisabled={formData.autoRegistrationEnabled}
                                                isSelected={formData.syncMeetingAttendeesWithZoomEnabled}
                                                onClick={formData.toggleSyncMeetingAttendees}
                                                dataTestId='webinar-autoSync-toggle'
                                            />
                                            <Text
                                                className={'m-left-8px'}
                                                message={formData.syncMeetingAttendeesWithZoomEnabled ? 'Sync Meeting Attendees with Zoom enabled' : 'Sync Meeting Attendees with Zoom disabled'}
                                                id={formData.syncMeetingAttendeesWithZoomEnabled ? 'oe.zoomWebinar.syncMeetingAttendeesWithZoomEnabled' : 'oe.zoomWebinar.syncMeetingAttendeesWithZoomDisabled'}
                                            />
                                            <InfoPopover className='m-left-8px'>
                                                <div>
                                                    {formVM.getConnectionVersion() === ConnectionVersion.V2
                                                        ? webinarSyncMeetingAttendeeInfoMessageV2
                                                        : webinarSyncMeetingAttendeeInfoMessage
                                                    }
                                                </div>
                                            </InfoPopover>
                                        </FormField>
                                    </>
                                )
                            }
                            <FormField className='form-toggle'>
                                <OEToggle
                                    isSelected={formData.registrationEnabled}
                                    onClick={formData.toggleRegistration}
                                    isDisabled={formData.syncMeetingAttendeesWithZoomEnabled}
                                    dataTestId='webinar-registration-toggle'
                                />
                                <Text
                                    className={'m-left-8px'}
                                    message={formData.registrationEnabled ? 'Registration enabled' : 'Registration disabled'}
                                    id={formData.registrationEnabled ? 'oe.zoomWebinar.registrationEnabled' : 'oe.zoomWebinar.registrationDisabled'}
                                />
                            </FormField>
                            {
                                formData.registrationEnabled &&
                                <FormField className='form-toggle'>
                                    <OEToggle
                                        isSelected={formData.approvalType === ApprovalType.AUTOMATICALLY}
                                        onClick={formData.toggleapprovalType}
                                        isDisabled={formData.syncMeetingAttendeesWithZoomEnabled}
                                        dataTestId='webinar-autoApproval-toggle'
                                    />
                                    <Text
                                        className={'m-left-8px'}
                                        message={formData.approvalType === ApprovalType.AUTOMATICALLY ? 'Automatic approval' : 'Manual approval'}
                                        id={formData.approvalType === ApprovalType.AUTOMATICALLY ? 'oe.zoomWebinar.automaticApproval' : 'oe.zoomWebinar.manualApproval'}
                                    />
                                </FormField>
                            }
                            {
                                formData.registrationEnabled &&
                                hasPermission(Permission.CREATE_ZOOM_CONNECTIONS) && (
                                    <FormField className='form-toggle'>
                                        <OEToggle
                                            onClick={formData.toggleEmailConfirmation}
                                            isDisabled={formData.autoRegistrationEnabled || formData.syncMeetingAttendeesWithZoomEnabled}
                                            isSelected={formData.registrantsConfirmationEmail}
                                            dataTestId='webinar-registrationEmail-toggle'
                                        />
                                        <Text
                                            className={'m-left-8px'}
                                            message={formData.registrantsConfirmationEmail ? 'Zoom registration emails enabled' : 'Zoom registration emails disabled'}
                                            id={formData.registrantsConfirmationEmail ? 'oe.zoomWebinar.emailNotificationEnabled' : 'oe.zoomWebinar.emailNotificationDisabled'}
                                        />
                                    </FormField>
                                )
                            }
                        </>
                    }
                </form>
            </CommonModal>
        </>
    );
});

export { CreateZoomWebinarModal };
